import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { HiArrowLeft, HiMail, HiPhone, HiLocationMarker, HiUsers, HiTicket } from 'react-icons/hi';
import { Button, Card, CardHeader, Table, Badge } from '../components/ui';
import { getPartner } from '../services/partners.api';
import { formatDate } from '../utils/helpers';

export function PartnerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: partner, isLoading } = useQuery({
    queryKey: ['partner', id],
    queryFn: () => getPartner(id!),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Partner not found</p>
        <Button variant="secondary" onClick={() => navigate('/partners')} className="mt-4">
          Back to Partners
        </Button>
      </div>
    );
  }

  const customerColumns = [
    { key: 'name', header: 'Name' },
    { key: 'email', header: 'Email' },
    { key: 'company', header: 'Company', render: (c: { company?: string }) => c.company || '-' },
    {
      key: 'isActive',
      header: 'Status',
      render: (c: { isActive: boolean }) => (
        <Badge status={c.isActive ? 'ACTIVE' : 'SUSPENDED'}>
          {c.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'licenses',
      header: 'Licenses',
      render: (c: { _count?: { licenses: number } }) => c._count?.licenses ?? 0,
    },
  ];

  const userColumns = [
    { key: 'email', header: 'Email' },
    {
      key: 'name',
      header: 'Name',
      render: (u: { firstName: string; lastName: string }) => `${u.firstName} ${u.lastName}`,
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (u: { isActive: boolean }) => (
        <Badge status={u.isActive ? 'ACTIVE' : 'SUSPENDED'}>
          {u.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
  ];

  const requestColumns = [
    {
      key: 'type',
      header: 'Type',
      render: (r: { type: string }) => r.type.replace(/_/g, ' '),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r: { status: string }) => (
        <Badge status={r.status as 'PENDING' | 'APPROVED' | 'REJECTED' | 'COMPLETED'}>
          {r.status}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (r: { createdAt: string }) => formatDate(r.createdAt),
    },
  ];

  return (
    <div>
      <Button
        variant="ghost"
        onClick={() => navigate('/partners')}
        className="mb-4"
      >
        <HiArrowLeft className="w-4 h-4 mr-2" />
        Back to Partners
      </Button>

      <CardHeader
        title={partner.name}
        description="Partner details and assigned customers"
      />

      {/* Partner Info */}
      <Card className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <HiMail className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="font-medium">{partner.email}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <HiPhone className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Phone</p>
              <p className="font-medium">{partner.phone || '-'}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <HiLocationMarker className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Address</p>
              <p className="font-medium">{partner.address || '-'}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <HiUsers className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <Badge status={partner.isActive ? 'ACTIVE' : 'SUSPENDED'}>
                {partner.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </div>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-6 text-sm text-gray-500">
          <span>Owner: {partner.owner?.name || 'None'}</span>
          <span>Created: {formatDate(partner.createdAt)}</span>
          <span>Customers: {partner._count?.customers ?? 0}</span>
          <span>Users: {partner._count?.users ?? 0}</span>
          <span>Requests: {partner._count?.requests ?? 0}</span>
        </div>
      </Card>

      {/* Assigned Customers */}
      <Card className="mb-6" padding={false}>
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <HiUsers className="w-5 h-5 text-gray-400" />
            Assigned Customers ({partner.customers?.length || 0})
          </h3>
        </div>
        <Table
          columns={customerColumns}
          data={partner.customers || []}
          keyExtractor={(customer) => customer.id}
          emptyMessage="No customers assigned to this partner"
          onRowClick={(customer) => navigate(`/customers/${customer.id}`)}
        />
      </Card>

      {/* Users with access */}
      <Card className="mb-6" padding={false}>
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <HiUsers className="w-5 h-5 text-gray-400" />
            Users mit Zugriff ({partner.users?.length || 0})
          </h3>
        </div>
        <Table
          columns={userColumns}
          data={partner.users || []}
          keyExtractor={(user) => user.id}
          emptyMessage="Kein Benutzer mit diesem Partner verknüpft."
        />
      </Card>

      {/* Recent Requests */}
      {partner.requests && partner.requests.length > 0 && (
        <Card padding={false}>
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <HiTicket className="w-5 h-5 text-gray-400" />
              Recent Requests ({partner.requests.length})
            </h3>
          </div>
          <Table
            columns={requestColumns}
            data={partner.requests}
            keyExtractor={(request) => request.id}
            emptyMessage="No requests from this partner"
          />
        </Card>
      )}
    </div>
  );
}
